/** Which tab's upload a file goes to; each creates the asset differently. */
export type UploadTarget = 'photos' | 'staged' | 'floorPlan';

export type DropTarget = UploadTarget | null;

/**
 * What each upload calls its files, for the drop overlay and the modal title.
 * A Floor Plan upload captions every file "Floor Plan" (DV-3735), so it must
 * not read as a Photos upload: room photos dropped there by mistake would all
 * become floor plans.
 */
export const UPLOAD_TARGET_NOUN: Record<UploadTarget, string> = {
  photos: 'Photos',
  staged: 'Photos',
  floorPlan: 'Floor Plans'
};

export interface DropTargetInput {
  isZillowActive: boolean;
  isStagedActive: boolean;
  isFloorPlanActive: boolean;
  isTrashActive: boolean;
  canUpload: boolean;
  canUploadStaged: boolean;
  canUploadFloorPlans: boolean;
}

/**
 * Which upload a page-level file drop feeds, given the active tab and the
 * user's capabilities — or null when this tab takes no drops.
 *
 * Gated per capability rather than on the combined canUseEditSuite() dial:
 * upload and virtual staging are diverging, and the Virtual Staging tab is
 * already visible to non-admins whenever a property has staged photos, so a
 * staged drop has to check the staged-upload capability specifically (canUploadStagedAssets).
 */
export function resolveDropTarget(input: DropTargetInput): DropTarget {
  if (input.isZillowActive) return null;
  // The Trash tab takes no drops (DV-2524). Suppressing only the drop overlay
  // would hide the affordance while the window listener still fed a photos
  // upload, so the inertness has to be decided here — the single owner of
  // "which upload a drop feeds".
  if (input.isTrashActive) return null;
  if (input.isStagedActive) return input.canUploadStaged ? 'staged' : null;
  if (input.isFloorPlanActive) {
    return input.canUploadFloorPlans ? 'floorPlan' : null;
  }
  return input.canUpload ? 'photos' : null;
}
